import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Printer, Smartphone, Bell, LifeBuoy, Info, HelpCircle, ShieldCheck } from 'lucide-react';
import {
  PhoneFrame, HomeScreen, SafariScreen, IosShareSheet, IosAddScreen, ChromeScreen,
  AndroidInstallDialog, LoginScreen, PushBannerScreen, PermissionDialog,
} from './PhoneMockups';
import { TestPushButton } from '@/components/TestPushButton';
import './MobileInstallGuide.css';

const URL_TEXT = 'cloud.vapelarm.se';

const IOS_STEPS = [
  { title: 'Öppna Safari', text: 'Leta upp den blå kompassikonen på hemskärmen och tryck på den. Installationen fungerar bäst i Safari.', screen: <HomeScreen os="ios" highlight="browser" /> },
  { title: `Gå till ${URL_TEXT}`, text: <>Tryck i adressfältet, skriv <b>{URL_TEXT}</b> och tryck på <b>Gå</b>.</>, screen: <SafariScreen highlight="url" /> },
  { title: 'Tryck på Dela-knappen', text: <>Tryck på <b>Dela</b> (fyrkant med uppåtpil) i verktygsfältet. I iOS 26 trycker du först på <b>•••</b> och väljer sedan <b>Dela</b>.</>, screen: <SafariScreen highlight="share" /> },
  { title: 'Välj ”Lägg till på hemskärmen”', text: 'Scrolla ner i delningsmenyn tills du ser alternativet och tryck på det. Hittar du det inte – tryck på ”Visa fler”.', screen: <IosShareSheet /> },
  { title: 'Tryck på ”Lägg till”', text: <>Namnet ”Vapelarm” fylls i automatiskt. Om reglaget <b>Öppna som webbapp</b> visas ska det vara påslaget. Tryck sedan <b>Lägg till</b> uppe till höger.</>, screen: <IosAddScreen /> },
  { title: 'Öppna appen från hemskärmen', text: 'En ny ikon – en röd varningstriangel på mörk bakgrund – finns nu på hemskärmen. Öppna alltid Vapelarm via den här ikonen.', screen: <HomeScreen os="ios" highlight="app" /> },
  { title: 'Logga in', text: 'Ange din e-postadress och ditt lösenord och tryck Logga in. Du behöver bara logga in första gången.', screen: <LoginScreen /> },
];

const ANDROID_STEPS = [
  { title: 'Öppna Google Chrome', text: 'Tryck på Chrome-ikonen (röd, gul och grön cirkel) på startskärmen eller i applistan.', screen: <HomeScreen os="android" highlight="browser" /> },
  { title: `Gå till ${URL_TEXT}`, text: <>Tryck i adressfältet högst upp, skriv <b>{URL_TEXT}</b> och tryck på Enter.</>, screen: <ChromeScreen highlight="url" /> },
  { title: 'Tryck på menyn med tre prickar', text: <>Menyknappen <b>⋮</b> finns uppe till höger, bredvid adressfältet.</>, screen: <ChromeScreen highlight="more" /> },
  { title: 'Välj ”Installera app”', text: <>Beroende på telefon och Chrome-version heter alternativet <b>Installera app</b> eller <b>Lägg till på startskärmen</b>.</>, screen: <ChromeScreen menu /> },
  { title: 'Bekräfta installationen', text: <>Tryck på <b>Installera</b> i rutan som visas. Om du valde ”Lägg till på startskärmen” trycker du på <b>Lägg till</b>.</>, screen: <AndroidInstallDialog /> },
  { title: 'Öppna appen via ikonen', text: 'Vapelarm-ikonen finns nu på startskärmen eller i applistan. Appen öppnas i helskärm utan webbläsarens adressfält.', screen: <HomeScreen os="android" highlight="app" /> },
  { title: 'Logga in', text: 'Ange din e-postadress och ditt lösenord och tryck Logga in.', screen: <LoginScreen /> },
];

const TROUBLE = {
  ios: [
    { q: 'Jag hittar inte installationsalternativet', a: 'Kontrollera att du använder Safari. Tryck på Dela (eller ••• → Dela i iOS 26) och scrolla ner i menyn. Saknas alternativet – tryck på ”Visa fler” eller ”Redigera åtgärder” och lägg till ”Lägg till på hemskärmen”.' },
    { q: 'Appikonen visas inte på hemskärmen', a: 'Svep åt vänster mellan hemskärmens sidor – ikonen hamnar på första lediga plats. Du kan också svepa nedåt på hemskärmen och söka efter ”Vapelarm”.' },
    { q: 'Jag kan inte logga in', a: 'Kontrollera att e-postadressen är rätt stavad. Tryck på ”Glömt lösenord?” på inloggningssidan för att få en återställningskod. Har du inget konto – kontakta skolans administratör.' },
    { q: 'Jag får inga pushnotiser', a: 'Pushnotiser kräver iOS 16.4 eller senare och att appen öppnas från hemskärmsikonen – inte i Safari. Kontrollera Inställningar → Notiser → Vapelarm → Tillåt notiser. Kontrollera även att Fokus/Stör ej inte blockerar notiser.' },
    { q: 'Hur öppnar jag appen igen?', a: 'Tryck på Vapelarm-ikonen på hemskärmen. Du förblir inloggad tills du loggar ut.' },
  ],
  android: [
    { q: 'Jag hittar inte installationsalternativet', a: 'Kontrollera att du använder Google Chrome och att Chrome är uppdaterad via Google Play. Leta efter ”Installera app” eller ”Lägg till på startskärmen” i menyn ⋮. Har appen redan installerats visas ”Öppna i app” i stället.' },
    { q: 'Appikonen visas inte på startskärmen', a: 'Öppna applistan (svep uppåt) och sök efter ”Vapelarm”. Håll fingret på ikonen och dra den till startskärmen.' },
    { q: 'Jag kan inte logga in', a: 'Kontrollera att e-postadressen är rätt stavad. Tryck på ”Glömt lösenord?” på inloggningssidan för att få en återställningskod. Har du inget konto – kontakta skolans administratör.' },
    { q: 'Jag får inga pushnotiser', a: 'Kontrollera Inställningar → Appar → Vapelarm (eller Chrome) → Aviseringar och att aviseringar är tillåtna. Kontrollera även att Stör ej är avstängt och att batterisparläget inte begränsar Chrome.' },
    { q: 'Hur öppnar jag appen igen?', a: 'Tryck på Vapelarm-ikonen på startskärmen eller i applistan. Du förblir inloggad tills du loggar ut.' },
  ],
};

function StepCard({ n, step, os }) {
  return (
    <div className="mig-step flex flex-col sm:flex-row items-center sm:items-start gap-5 sm:gap-8 py-7 border-b border-slate-100 dark:border-slate-800 last:border-0" data-testid={`install-step-${os}-${n}`}>
      <PhoneFrame os={os}>{step.screen}</PhoneFrame>
      <div className="flex-1 sm:pt-6">
        <div className="flex items-center gap-3 mb-2">
          <span className="w-8 h-8 rounded-full bg-navy text-white flex items-center justify-center text-sm font-bold font-chivo shrink-0">{n}</span>
          <h3 className="font-chivo font-bold text-base text-navy dark:text-white">{step.title}</h3>
        </div>
        <p className="text-sm text-slate-600 dark:text-slate-300 font-ibm leading-relaxed sm:pl-11">{step.text}</p>
      </div>
    </div>
  );
}

function SectionTitle({ icon: Icon, children, id }) {
  return (
    <div className="mig-section-title flex items-center gap-2.5 mt-10 mb-4" id={id}>
      <div className="w-8 h-8 rounded-lg bg-red-50 dark:bg-red-900/20 flex items-center justify-center"><Icon className="h-4 w-4 text-red-600" /></div>
      <h2 className="font-chivo font-black text-lg text-navy dark:text-white tracking-tight">{children}</h2>
    </div>
  );
}

function Callout({ children }) {
  return (
    <div className="flex gap-3 rounded-xl border border-blue-200 bg-blue-50/70 dark:bg-blue-900/10 dark:border-blue-900 p-4 my-4 text-sm font-ibm text-slate-700 dark:text-slate-300 break-inside-avoid">
      <Info className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" /><div className="leading-relaxed">{children}</div>
    </div>
  );
}

function PushSection({ os }) {
  const ios = os === 'ios';
  return (
    <div className="mig-push">
      <SectionTitle icon={Bell}>Aktivera pushnotiser</SectionTitle>
      <p className="text-sm text-slate-600 dark:text-slate-300 font-ibm leading-relaxed mb-2">
        Med pushnotiser får du larm direkt i telefonen – även när appen är stängd. Tryck på notisen för att öppna larmet i Vapelarm Cloud.
      </p>
      {ios && <Callout>På iPhone fungerar pushnotiser <b>endast</b> när Vapelarm Cloud är installerad på hemskärmen och öppnas via ikonen. Telefonen behöver <b>iOS 16.4 eller senare</b>. I Safari visas i stället en påminnelse om att lägga till appen på hemskärmen.</Callout>}
      <div className="grid sm:grid-cols-2 gap-6 my-6 justify-items-center">
        <div className="flex flex-col items-center gap-3 text-center">
          <PhoneFrame os={os}><PushBannerScreen /></PhoneFrame>
          <p className="text-sm font-ibm text-slate-600 dark:text-slate-300 max-w-[240px]"><b className="text-navy dark:text-white">1.</b> Efter inloggning visas en mörkblå ruta högst upp. Tryck på <b>Aktivera</b>.</p>
        </div>
        <div className="flex flex-col items-center gap-3 text-center">
          <PhoneFrame os={os}><PermissionDialog os={os} /></PhoneFrame>
          <p className="text-sm font-ibm text-slate-600 dark:text-slate-300 max-w-[240px]"><b className="text-navy dark:text-white">2.</b> Telefonen frågar om tillstånd. Tryck på <b>Tillåt</b>. En bekräftelse visas i appen.</p>
        </div>
      </div>
      <div className="print:hidden rounded-xl border border-emerald-200 bg-emerald-50/60 dark:bg-emerald-900/10 dark:border-emerald-900 p-4 mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="text-sm font-ibm text-slate-700 dark:text-slate-300">
          <p className="font-semibold text-navy dark:text-white">3. Testa att det fungerar</p>
          <p className="text-slate-500 mt-0.5">Skicka en testnotis till den här telefonen. Tryck på notisen för att öppna appen.</p>
        </div>
        <TestPushButton testId={`test-push-btn-guide-${os}`} />
      </div>
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 text-sm font-ibm text-slate-600 dark:text-slate-300 break-inside-avoid">
        <p className="font-semibold text-navy dark:text-white mb-1.5 flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Kontrollera telefonens behörighet</p>
        {ios
          ? <p>Öppna <b>Inställningar → Notiser → Vapelarm</b> och se till att <b>Tillåt notiser</b> är påslaget. Välj gärna Ljud och Banners.</p>
          : <p>Öppna <b>Inställningar → Appar → Vapelarm → Aviseringar</b> och se till att aviseringar är tillåtna. På vissa telefoner styrs detta i stället under <b>Chrome → Aviseringar</b>.</p>}
        <p className="mt-2 text-slate-500">Har du tryckt på krysset i rutan visas den inte igen på den telefonen. Den mörkblå rutan visas inte heller om notiserna redan är aktiverade.</p>
      </div>
    </div>
  );
}

function Troubleshooting({ os }) {
  return (
    <div className="mig-trouble">
      <SectionTitle icon={LifeBuoy}>Felsökning</SectionTitle>
      <div className="space-y-2">
        {TROUBLE[os].map((t, i) => (
          <details key={i} open className="group border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden break-inside-avoid" data-testid={`trouble-${os}-${i}`}>
            <summary className="flex items-center gap-3 p-4 cursor-pointer font-ibm font-semibold text-sm text-navy dark:text-white select-none">
              <HelpCircle className="h-4 w-4 text-red-500 shrink-0" /> {t.q}
            </summary>
            <p className="px-4 pb-4 pl-11 text-sm text-slate-500 dark:text-slate-400 font-ibm leading-relaxed">{t.a}</p>
          </details>
        ))}
      </div>
      <p className="text-xs text-slate-400 font-ibm mt-4">Fortfarande problem? Kontakta skolans administratör eller support@vapelarm.se.</p>
    </div>
  );
}

function OsPanel({ os, active }) {
  const steps = os === 'ios' ? IOS_STEPS : ANDROID_STEPS;
  return (
    <section className={`mig-panel ${active ? '' : 'hidden'}`} data-testid={`install-panel-${os}`}>
      <h2 className="mig-print-heading hidden font-chivo font-black text-xl text-navy mb-2">{os === 'ios' ? 'iPhone – installera via Safari' : 'Android – installera via Chrome'}</h2>
      {os === 'ios'
        ? <Callout>Gäller iPhone med iOS 16.4 eller senare. Utseendet skiljer sig något mellan iOS-versioner – i <b>iOS 26</b> nås Dela via knappen <b>•••</b> i adressfältet.</Callout>
        : <Callout>Gäller Android-telefoner med Google Chrome. Ibland visar Chrome eller Vapelarm Cloud (under Notifikationer) en egen <b>Installera</b>-knapp – då kan du trycka direkt på den.</Callout>}
      <div className="mig-steps">{steps.map((s, i) => <StepCard key={i} n={i + 1} step={s} os={os} />)}</div>
      <PushSection os={os} />
      <Troubleshooting os={os} />
    </section>
  );
}

const TABS = [
  { id: 'ios', label: 'iPhone', sub: 'Safari' },
  { id: 'android', label: 'Android', sub: 'Chrome' },
];

export function MobileInstallGuide() {
  const [os, setOs] = useState(() => (/Android/i.test(navigator.userAgent) ? 'android' : 'ios'));

  const handlePrint = () => {
    document.documentElement.classList.add('mig-printing');
    const cleanup = () => { document.documentElement.classList.remove('mig-printing'); window.removeEventListener('afterprint', cleanup); };
    window.addEventListener('afterprint', cleanup);
    window.print();
  };

  return (
    <div id="mig-root" className="mobile-install-guide" data-testid="mobile-install-guide">
      <div className="mig-hero relative overflow-hidden rounded-2xl bg-navy text-white p-6 md:p-8 mb-8">
        <div className="absolute -right-10 -top-10 w-48 h-48 rounded-full bg-red-500/20 blur-3xl" />
        <div className="relative flex items-start gap-4">
          <img src="/icon-192.png" alt="Vapelarm" className="w-10 h-10 sm:w-14 sm:h-14 rounded-2xl shadow-lg ring-1 ring-white/10" />
          <div className="flex-1">
            <p className="text-[11px] uppercase tracking-[0.2em] text-red-300 font-ibm font-semibold">Vapelarm Cloud · Installationsguide</p>
            <h2 className="font-chivo font-black text-2xl md:text-3xl tracking-tight mt-1">Installera appen på din telefon</h2>
            <p className="text-sm text-slate-300 font-ibm mt-2 max-w-lg leading-relaxed">Ingen App Store eller Google Play behövs. Det tar ungefär två minuter – följ stegen för din telefon.</p>
          </div>
        </div>
        <div className="relative flex flex-wrap gap-2 mt-5 text-xs font-ibm">
          <span className="px-3 py-1 rounded-full bg-white/10 border border-white/10">{URL_TEXT}</span>
          <span className="px-3 py-1 rounded-full bg-white/10 border border-white/10">ca 2 minuter</span>
          <span className="px-3 py-1 rounded-full bg-white/10 border border-white/10">Kostnadsfritt</span>
        </div>
      </div>

      <div className="mig-toolbar flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-2">
        <div className="inline-flex p-1 rounded-xl bg-slate-100 dark:bg-slate-800" role="tablist">
          {TABS.map(t => (
            <button key={t.id} role="tab" aria-selected={os === t.id} onClick={() => setOs(t.id)}
              className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-ibm font-semibold transition-colors ${os === t.id ? 'bg-white dark:bg-slate-900 text-navy dark:text-white shadow-sm' : 'text-slate-500 hover:text-navy'}`}
              data-testid={`install-tab-${t.id}`}>
              <Smartphone className="h-4 w-4" />{t.label}<span className="hidden sm:inline text-xs font-normal text-slate-400">{t.sub}</span>
            </button>
          ))}
        </div>
        <Button variant="outline" onClick={handlePrint} className="gap-2" data-testid="install-guide-print-btn">
          <Printer className="h-4 w-4" /> Skriv ut / spara som PDF
        </Button>
      </div>

      <OsPanel os="ios" active={os === 'ios'} />
      <OsPanel os="android" active={os === 'android'} />
    </div>
  );
}
